import React, { useEffect } from 'react'
import { useMsal, useIsAuthenticated } from '@azure/msal-react'
import { Navigate, useLocation } from 'react-router-dom'
import Button2 from '@embracesbs/component-button2/Button2'
import Icon from '@embracesbs/component-icon/Icon'
import Text from '@embracesbs/component-text'
import { useConfig } from '../Components/Config/ConfigContext'
import TmaLogo from '../Components/Common/TmaLogo'

const Login: React.FC = () => {
  const { instance } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const location = useLocation()
  const config = useConfig()

  // Store the redirect URL as soon as the login page loads
  useEffect(() => {
    const from = (location.state as any)?.from
    if (from && from !== '/') {
      console.log('[Login] Storing redirect URL on mount:', from)
      sessionStorage.setItem('redirectAfterLogin', from)
    }
  }, [location.state])

  // If already authenticated, redirect to the original page or dashboard
  if (isAuthenticated) {
    // Get the redirect URL - DON'T clear it here, let App.tsx handle that
    const redirectTo = (location.state as any)?.from || sessionStorage.getItem('redirectAfterLogin') || '/'
    console.log('[Login] User authenticated, redirecting to:', redirectTo)
    // Don't remove sessionStorage here - it causes a race condition
    return <Navigate to={redirectTo} replace />
  }

  const handleLogin = () => {
    console.log('[Login] Login button clicked, redirecting to Microsoft')
    instance.loginRedirect().catch((e) => {
      console.error('Login failed:', e)
    })
  }

  // Get individual environment badges
  const getEnvironmentBadges = () => {
    const environments = config?.environments || []

    // Determine environment type
    const isNonproduction = environments.some((env) => ['alpha', 'stable-alpha'].includes(env))
    const isProduction = environments.some((env) => ['beta', 'production'].includes(env))

    const type = isNonproduction ? 'Nonproduction' : isProduction ? 'Production' : 'Unknown'
    const color = '#416eec' // Use the same blue as the page background

    // Create individual badges for each environment
    const badges = environments.map((env) => {
      const label = env
        .replace(/-/g, ' ')
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
      return { label, env }
    })

    return { type, color, badges }
  }

  const envData = getEnvironmentBadges()

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        width: '100vw',
        background: 'var(--tma-band-bg)',
        position: 'fixed',
        top: 0,
        left: 0
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'white',
          borderRadius: '12px',
          padding: '60px 80px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
          maxWidth: '500px',
          width: '90%'
        }}
      >
        {/* Logo */}
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <TmaLogo height={64} color='#4a7dff' accentColor='#1d3fae' />
        </div>

        <div style={{ marginBottom: '12px', width: '100%' }}>
          <Text
            textColor='#1a1a1a'
            style={{
              fontSize: '28px',
              fontWeight: '700',
              textAlign: 'center',
              display: 'block'
            }}
          >
            Tenants Management Application
          </Text>
        </div>

        <div style={{ marginBottom: '20px', width: '100%' }}>
          <Text
            textColor='#666'
            style={{
              fontSize: '15px',
              textAlign: 'center',
              lineHeight: '1.5',
              display: 'block'
            }}
          >
            Manage your subscriptions and tenants
          </Text>
        </div>

        {/* Environment Badges */}
        <div
          style={{
            marginBottom: '30px',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap'
          }}
        >
          {envData.badges.map((badge, index) => (
            <div
              key={index}
              style={{
                padding: '10px 18px',
                borderRadius: '6px',
                background: `${envData.color}15`,
                border: `1px solid ${envData.color}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <Text
                textColor={envData.color}
                style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  display: 'block',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  opacity: 0.8
                }}
              >
                {envData.type}
              </Text>
              <Text
                textColor={envData.color}
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'block'
                }}
              >
                {badge.label}
              </Text>
            </div>
          ))}
        </div>

        <Button2
          leftElement={<Icon iconName='Login2' size={20} color='white' />}
          onClick={handleLogin}
          text='Sign in with Microsoft'
          variant='contained'
          buttonSize='50px'
          style={{
            width: '100%',
            fontSize: '16px',
            fontWeight: '600',
            background: '#416eec',
            border: 'none'
          }}
        />

        <div style={{ marginTop: '24px', width: '100%' }}>
          <Text
            textColor='#999'
            style={{
              fontSize: '12px',
              textAlign: 'center',
              display: 'block'
            }}
          >
            You will be redirected to Microsoft to sign in
          </Text>
        </div>
      </div>
    </div>
  )
}

export default Login
